import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { DateFormat } from '~/i18n/hooks/useFormatDate/utils';
import * as toastTypes from '~/types/toast.types';

import { editDocument } from '../../Document.data';
import { preprocessEditData, processSectionItems } from '../helpers';
import { EditData, OverviewSection } from '../types';

interface UseDocumentEditProps {
  documentId: number;
  sections: OverviewSection[];
  onEditSubmitted: () => void;
}

export const useDocumentEdit = ({
  documentId,
  sections,
  onEditSubmitted,
}: UseDocumentEditProps) => {
  const intl = useIntl();
  const formatDate = useFormatDate();

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<EditData>({});
  const [isSaving, setIsSaving] = useState(false);

  const initializeEditData = useCallback(() => {
    setEditData(processSectionItems(sections, formatDate));
  }, [sections, formatDate]);

  const handleDiscardChanges = useCallback(() => {
    setEditMode(false);
    setEditData(processSectionItems(sections, formatDate));
  }, [sections, formatDate]);

  const handleSaveChanges = useCallback(() => {
    const requestData = preprocessEditData(editData);
    setIsSaving(true);
    editDocument(documentId, requestData)
      .catch(() => {
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'DocumentOverviewPanel.editFieldsError',
            defaultMessage: 'An error occurred while editing the field(s).',
            description: 'Error message when editing document fields fails',
          }),
        );
      })
      .finally(() => {
        setIsSaving(false);
        setEditMode(false);
        onEditSubmitted();
      });
  }, [editData, documentId, intl, onEditSubmitted]);

  const handleEditDataChange = useCallback((fieldId: number, value: any) => {
    setEditData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  }, []);

  const handleDateChange = useCallback(
    (date: Date | null, fieldId: number, dateFormat: DateFormat) => {
      setEditData((prevState) => ({
        ...prevState,
        [fieldId]: date ? formatDate(date, dateFormat) : null,
      }));
    },
    [formatDate],
  );

  const handleFileAttachError = useCallback(() => {
    showToast(
      toastTypes.ERROR,
      intl.formatMessage({
        id: 'DocumentOverviewPanel.attachFileError',
        defaultMessage: 'Attach file failed. Please try again.',
        description: 'Error message when file attachment fails',
      }),
    );
  }, [intl]);

  return {
    editMode,
    setEditMode,
    editData,
    isSaving,
    initializeEditData,
    handleDiscardChanges,
    handleSaveChanges,
    handleEditDataChange,
    handleDateChange,
    handleFileAttachError,
  };
};
